// @swaralo/widget: call a Swaralo agent from a web page (V1-E03-T04).
//
// Your server asks Swaralo for a client credential (POST /v1/client-credentials, with your API key: one
// agent, some minutes, your site's origins) and gives the token to the page. The page calls:
//
//   const call = await startCall({ apiUrl: "https://swaralo.example.com", token, onTranscript, onState });
//   call.hangUp();
//
// The token is redeemed for a SIP login (only from your origins, only until it expires); the call goes
// over WebRTC to the Swaralo edge, and the transcript comes over the credential's own event stream.

import { Inviter, SessionState, UserAgent } from "sip.js";
import type { Session } from "sip.js";

export type CallState = "connecting" | "ringing" | "in-call" | "ended" | "failed";

export interface Turn {
  seq: number;
  role: "user" | "assistant";
  text: string;
}

export interface CallOptions {
  /** The Swaralo API's base URL. */
  apiUrl: string;
  /** From your server: POST /v1/client-credentials's `token`. */
  token: string;
  /** Where the agent is heard; one is made (and removed after) if not given. */
  audio?: HTMLAudioElement;
  onTranscript?: (turn: Turn) => void;
  onState?: (state: CallState, reason?: string) => void;
}

export interface Call {
  readonly state: CallState;
  hangUp(): Promise<void>;
  /** Mute or unmute the page's microphone. */
  mute(muted: boolean): void;
  /** Keypad digits (RFC 4733), e.g. a supervisor changing mode: 0 listen, 1 whisper, 3 barge. */
  dtmf(digits: string): void;
}

/** A SIP login to call with: a client credential's (redeemed), or a supervisor's (V1-E04-T04). */
export interface SipLogin {
  sip: { wss_url: string; domain: string; username: string; password: string; call: string };
  ice_servers: { urls: string[]; username?: string | null; credential?: string | null }[];
}

export interface ConnectOptions {
  audio?: HTMLAudioElement;
  onState?: (state: CallState, reason?: string) => void;
  /** Called when the call ends, after its own cleanup. */
  onEnd?: () => void;
}

/** What redeeming the token gives (POST /v1/client-credentials/session). */
export interface ClientSession {
  sip: { wss_url: string; domain: string; username: string; password: string; call: string };
  ice_servers: { urls: string[]; username?: string | null; credential?: string | null }[];
  expires_at: string;
  stream_url: string;
}

export class WidgetError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "WidgetError";
  }
}

/** Redeem the token: refused (403) unless this page's origin is one of the credential's, before it expires. */
export async function redeem(apiUrl: string, token: string, fetcher: typeof fetch = fetch): Promise<ClientSession> {
  const response = await fetcher(`${apiUrl.replace(/\/+$/, "")}/v1/client-credentials/session`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ token }),
  });
  if (!response.ok) {
    let detail = "";
    try {
      detail = ((await response.json()) as { detail?: string }).detail ?? "";
    } catch {
      // not JSON: the status says enough
    }
    throw new WidgetError(detail || `the credential was refused (HTTP ${response.status})`, response.status);
  }
  return (await response.json()) as ClientSession;
}

/** A `transcript.final` event of the stream, as a turn; anything else, null. */
export function turnOf(message: string): Turn | null {
  try {
    const event = JSON.parse(message) as { type?: string; data?: Partial<Turn> };
    const d = event.data;
    if (event.type !== "transcript.final" || !d || typeof d.text !== "string" || typeof d.seq !== "number") return null;
    if (d.role !== "user" && d.role !== "assistant") return null;
    return { seq: d.seq, role: d.role, text: d.text };
  } catch {
    return null;
  }
}

export async function startCall(options: CallOptions): Promise<Call> {
  options.onState?.("connecting");
  let session: ClientSession;
  try {
    session = await redeem(options.apiUrl, options.token);
  } catch (e) {
    options.onState?.("failed", e instanceof Error ? e.message : String(e));
    throw e;
  }

  // the transcript: this credential's calls' events
  const seen = new Set<number>();
  const stream = new WebSocket(`${session.stream_url}?token=${encodeURIComponent(options.token)}`);
  stream.onmessage = (m) => {
    const turn = typeof m.data === "string" ? turnOf(m.data) : null;
    if (turn && !seen.has(turn.seq)) {
      seen.add(turn.seq);
      options.onTranscript?.(turn);
    }
  };
  await new Promise<void>((done) => {
    stream.onopen = () => done();
    stream.onerror = () => done(); // no transcript, but the call itself may still go
    setTimeout(done, 3000);
  });
  return connect(session, { audio: options.audio, onState: options.onState, onEnd: () => stream.close() });
}

/** Call with a SIP login already in hand (a supervisor's, from POST /v1/calls/{id}/monitor). */
export async function connect(session: SipLogin, options: ConnectOptions = {}): Promise<Call> {
  let state: CallState = "connecting";
  const set = (next: CallState, reason?: string) => {
    if (state === "ended" || state === "failed") return;
    state = next;
    options.onState?.(next, reason);
  };
  const ua = new UserAgent({
    uri: UserAgent.makeURI(`sip:${session.sip.username}@${session.sip.domain}`),
    transportOptions: { server: session.sip.wss_url },
    authorizationUsername: session.sip.username,
    authorizationPassword: session.sip.password,
    sessionDescriptionHandlerFactoryOptions: {
      peerConnectionConfiguration: { iceServers: session.ice_servers.map(toIceServer) },
    },
    logLevel: "error",
  });
  const audio = options.audio ?? document.body.appendChild(document.createElement("audio"));
  audio.autoplay = true;
  let inviter: Inviter | undefined;
  const finish = async () => {
    options.onEnd?.();
    await ua.stop().catch(() => undefined);
    if (!options.audio) audio.remove();
  };

  try {
    await ua.start();
    const target = UserAgent.makeURI(session.sip.call);
    if (!target) throw new WidgetError(`not a SIP URI: ${session.sip.call}`);
    inviter = new Inviter(ua, target, {
      sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
    });
    inviter.stateChange.addListener((s) => {
      if (s === SessionState.Establishing) set("ringing");
      if (s === SessionState.Established) {
        audio.srcObject = remoteStream(inviter as Session);
        void audio.play().catch(() => undefined);
        set("in-call");
      }
      if (s === SessionState.Terminated) {
        set(state === "in-call" ? "ended" : "failed", state === "in-call" ? undefined : "the call did not connect");
        void finish();
      }
    });
    await inviter.invite();
  } catch (e) {
    set("failed", e instanceof Error ? e.message : String(e));
    await finish();
    throw e;
  }

  return {
    get state() {
      return state;
    },
    async hangUp() {
      if (!inviter) return;
      if (inviter.state === SessionState.Established) await inviter.bye();
      else if (inviter.state === SessionState.Establishing || inviter.state === SessionState.Initial)
        await inviter.cancel();
    },
    mute(muted: boolean) {
      const pc = peerConnection(inviter);
      pc?.getSenders().forEach((s) => {
        if (s.track) s.track.enabled = !muted;
      });
    },
    dtmf(digits: string) {
      const sender = peerConnection(inviter)?.getSenders().find((s) => s.dtmf)?.dtmf;
      sender?.insertDTMF(digits, 120, 80);
    },
  };
}

function toIceServer(s: SipLogin["ice_servers"][number]): RTCIceServer {
  const server: RTCIceServer = { urls: s.urls };
  if (s.username) server.username = s.username;
  if (s.credential) server.credential = s.credential;
  return server;
}

function peerConnection(session: Session | undefined): RTCPeerConnection | undefined {
  const handler = session?.sessionDescriptionHandler as { peerConnection?: RTCPeerConnection } | undefined;
  return handler?.peerConnection;
}

function remoteStream(session: Session): MediaStream {
  const stream = new MediaStream();
  peerConnection(session)
    ?.getReceivers()
    .forEach((r) => r.track && stream.addTrack(r.track));
  return stream;
}
